import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";

const TENETS = [
  {
    n: "I",
    title: "Set the ego aside",
    body: "The House of Ego grew out of a simple question: what becomes possible when we set the ego aside? The ego can both protect and limit us; creative expression becomes a pathway toward deeper understanding and transformation.",
  },
  {
    n: "II",
    title: "Permission to feel",
    body: "At its core, The House of Ego gives people permission to feel, to express, to question, to explore, and to heal. It is a journey through the layers of the self.",
  },
  {
    n: "III",
    title: "Art as mirror, not spectacle",
    body: "Each body of work begins as a personal excavation and becomes an invitation for the viewer to locate their own story inside it.",
  },
  {
    n: "IV",
    title: "Heal yourself first",
    body: "Art as activism teaches generations the importance of self-love and self-advocacy, grounding the belief that we must first heal ourselves to truly show up for our community.",
  },
  {
    n: "V",
    title: "Show up intentionally",
    body: "The practice is to continue showing up intentionally in how we create, how we process what we feel, and how we respond.",
  },
];

export default function PhilosophyPage() {
  useSEO(
    "Philosophy · The House of Ego",
    "The philosophy of The House of Ego: the transcendence of ego through creative expression, art as mirror, healing, and self-advocacy, by artist and curator Seyoun Taylor."
  );

  return (
    <main id="main-content" data-testid="philosophy-page">
      <section className="mx-auto max-w-7xl px-6 md:px-10 pt-40 md:pt-52 pb-20">
        <Reveal>
          <p className="eyebrow">The Philosophy</p>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight text-ink max-w-4xl leading-[1.05]">
            What becomes possible when we set the ego aside?
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-neutral-600">
            The House of Ego is more than a platform. It is a movement of healing and awareness through
            storytelling, welcoming visitors from every background and every stage of their personal
            journey.
          </p>
        </Reveal>
      </section>

      <section className="bg-royal-soft" data-testid="movement-quote-section">
        <div className="mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <blockquote className="font-serif text-2xl md:text-4xl leading-snug tracking-tight text-royal-deep">
              “I am creating a movement that bridges the gap of showing up as we are to showing up in
              who we can be when we see ourselves before the world tries to tell us who we are.”
            </blockquote>
            <p className="mt-6 text-sm text-neutral-600">Seyoun Taylor</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28" data-testid="tenets-section">
        <Reveal>
          <p className="eyebrow">Five tenets</p>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
            How the House of Ego stands
          </h2>
        </Reveal>
        <div className="mt-14">
          {TENETS.map((t, i) => (
            <Reveal key={t.n} delay={i * 0.06}>
              <div className="grid md:grid-cols-[100px_240px_1fr] gap-4 md:gap-10 py-10 border-t border-neutral-200 last:border-b items-baseline">
                <p className="font-serif text-3xl text-flame">{t.n}</p>
                <h3 className="font-serif text-2xl text-ink">{t.title}</h3>
                <p className="text-neutral-600 leading-relaxed max-w-2xl">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 md:px-10 pb-24 md:pb-32" data-testid="philosophy-cta-section">
        <Reveal>
          <div className="bg-azure-soft px-8 md:px-14 py-14 md:py-20 grid gap-8 lg:grid-cols-[1fr_auto] items-center">
            <div>
              <p className="eyebrow !text-azure">Experience it</p>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-ink max-w-xl">
                The philosophy lives in the work.
              </h2>
            </div>
            <Link
              to="/gallery"
              data-testid="philosophy-cta-gallery"
              className="bg-azure text-white px-8 py-4 text-sm tracking-wide hover:bg-royal transition-colors inline-flex items-center gap-2 w-fit"
            >
              Enter the Gallery <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
